import { reactive, computed } from 'vue'
import ApiService from '@/core/services/ApiService'
import { endpoints } from '@/core/services/Endpoints'
import UserConfigService from '@/core/services/UserConfigService'

const NS = 'weatherPanel'
const MAX_LOCATIONS = 10
const REFRESH_INTERVAL = 15 * 60 * 1000

const state = reactive({
    locations: [],
    defaultIndex: 0,
    activeIndex: 0,
    dockAnimation: true,
    _refreshTimer: null,
    _initialized: false,
    _demo: false,
    _ready: false,
})

function loadSaved() {
    const saved = UserConfigService.get(NS)
    if (saved.locations?.length) {
        state.locations = saved.locations.map(loc => ({
            cityId: loc.cityId,
            districtId: loc.districtId,
            cityName: loc.cityName,
            districtName: loc.districtName,
            data: null,
            status: 'idle',
        }))
        state.defaultIndex = saved.defaultIndex ?? 0
        if (state.defaultIndex >= state.locations.length) state.defaultIndex = 0
    }
    if (saved.dockAnimation != null) state.dockAnimation = saved.dockAnimation
}

function save() {
    if (state._demo) return
    UserConfigService.set(NS, {
        locations: state.locations.map(l => ({
            cityId: l.cityId,
            districtId: l.districtId,
            cityName: l.cityName,
            districtName: l.districtName,
        })),
        defaultIndex: state.defaultIndex,
        dockAnimation: state.dockAnimation,
    })
}

async function fetchWeather(index) {
    const loc = state.locations[index]
    if (!loc) return
    loc.status = 'loading'
    try {
        const params = { city_id: loc.cityId }
        if (loc.districtId) params.district_id = loc.districtId
        const res = await ApiService.axios.get(endpoints.weather_lookup, { params })
        const d = res?.data?.data
        if (d?.status === 'pending' || !d?.item) {
            loc.data = null
            loc.status = 'pending'
        } else {
            loc.data = d.item
            loc.status = 'loaded'
        }
    } catch {
        loc.status = 'error'
    }
}

async function fetchAll() {
    const promises = state.locations.map((_, i) => fetchWeather(i))
    await Promise.allSettled(promises)
}

function addLocation(cityId, districtId, cityName, districtName) {
    if (state._demo) {
        state.locations = []
        state.defaultIndex = 0
        state.activeIndex = 0
        state._demo = false
    }
    if (state.locations.length >= MAX_LOCATIONS) return false
    const exists = state.locations.some(l => l.cityId === cityId && l.districtId === districtId)
    if (exists) return false
    state.locations.push({
        cityId,
        districtId,
        cityName,
        districtName,
        data: null,
        status: 'idle',
    })
    const idx = state.locations.length - 1
    fetchWeather(idx)
    save()
    return true
}

function removeLocation(index) {
    if (index < 0 || index >= state.locations.length) return
    state.locations.splice(index, 1)
    if (state.defaultIndex >= state.locations.length) {
        state.defaultIndex = Math.max(0, state.locations.length - 1)
    }
    if (state.defaultIndex > index) state.defaultIndex--
    if (state.activeIndex >= state.locations.length) {
        state.activeIndex = Math.max(0, state.locations.length - 1)
    }
    if (state.activeIndex > index) state.activeIndex--
    save()
}

function setDefault(index) {
    if (index < 0 || index >= state.locations.length) return
    state.defaultIndex = index
    save()
}

function setActive(index) {
    if (index < 0 || index >= state.locations.length) return
    state.activeIndex = index
    fetchIfNeeded(index)
}

function fetchLoaded() {
    const promises = state.locations
        .map((l, i) => l.status === 'loaded' ? fetchWeather(i) : null)
        .filter(Boolean)
    return Promise.allSettled(promises)
}

function startAutoRefresh() {
    stopAutoRefresh()
    state._refreshTimer = setInterval(() => fetchLoaded(), REFRESH_INTERVAL)
}

function stopAutoRefresh() {
    if (state._refreshTimer) {
        clearInterval(state._refreshTimer)
        state._refreshTimer = null
    }
}

async function searchCity(query) {
    if (!query || query.length < 2) return []
    try {
        const res = await ApiService.axios.get(endpoints.weather_location_search, {
            params: { q: query },
        })
        const results = res?.data?.data || []
        const cityIds = new Set(results.filter(r => !r.district_id).map(r => r.city_id))
        return results.filter(r => !(r.district_id && cityIds.has(r.city_id) && r.label.startsWith('Merkez')))
    } catch {
        return []
    }
}

async function detectLocation() {
    return new Promise((resolve, reject) => {
        if (!navigator.geolocation) return reject(new Error('no_geolocation'))
        navigator.geolocation.getCurrentPosition(
            async (pos) => {
                try {
                    const { latitude, longitude } = pos.coords
                    const url = `https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json&accept-language=tr`
                    const geoRes = await fetch(url)
                    const geoData = await geoRes.json()
                    const cityName = geoData?.address?.province || geoData?.address?.city || geoData?.address?.state
                    if (!cityName) return reject(new Error('no_city'))
                    const results = await searchCity(cityName)
                    if (results.length) {
                        const r = results[0]
                        const parts = r.label.split(', ')
                        addLocation(r.city_id, r.district_id, parts.length > 1 ? parts[1] : parts[0], parts[0])
                        if (state.locations.length === 1) setDefault(0)
                        resolve(r)
                    } else {
                        reject(new Error('no_results'))
                    }
                } catch (e) {
                    reject(e)
                }
            },
            (err) => reject(err),
            { timeout: 10000 }
        )
    })
}

function loadDemo() {
    const demos = [
        { city: 'Antalya', cityId: 7, icon: 'clear', id: 0, label: 'Açık', temp: 28, min: 22, max: 30 },
        { city: 'İzmir', cityId: 35, icon: 'mainly_clear', id: 1, label: 'Güneşli', temp: 26, min: 20, max: 29 },
        { city: 'İstanbul', cityId: 34, icon: 'partly_cloudy', id: 2, label: 'Parçalı Bulutlu', temp: 14, min: 10, max: 16 },
        { city: 'Ankara', cityId: 6, icon: 'overcast', id: 3, label: 'Bulutlu', temp: 6, min: 2, max: 8 },
        { city: 'Trabzon', cityId: 61, icon: 'rain', id: 63, label: 'Yağmurlu', temp: 9, min: 6, max: 11 },
        { city: 'Erzurum', cityId: 25, icon: 'snow', id: 73, label: 'Karlı', temp: -4, min: -8, max: -1 },
        { city: 'Adana', cityId: 1, icon: 'thunderstorm', id: 95, label: 'Gök Gürültülü', temp: 18, min: 14, max: 22 },
        { city: 'Bolu', cityId: 14, icon: 'fog', id: 45, label: 'Sisli', temp: 3, min: 0, max: 5 },
        { city: 'Muğla', cityId: 48, icon: 'night_clear', id: 0, label: 'Açık (Gece)', temp: 16, min: 12, max: 20 },
    ]
    const today = new Date()
    state.locations = demos.map(d => ({
        cityId: d.cityId,
        districtId: null,
        cityName: d.city,
        districtName: d.city,
        status: 'loaded',
        data: {
            current: {
                temperature: d.temp,
                feels_like: d.temp - 2,
                humidity: 60,
                wind_speed: 12,
                uv_index: 3,
                pressure: 1013,
                visibility: 10,
                condition: { id: d.id, icon: d.icon, label: d.label },
            },
            hourly: Array.from({ length: 24 }, (_, i) => ({
                time: '2026-02-26T' + String(i).padStart(2, '0') + ':00',
                temperature: d.temp + Math.round(Math.sin(i / 4) * 3),
                condition: { id: d.id, icon: d.icon },
            })),
            daily: Array.from({ length: 7 }, (_, i) => {
                const date = new Date(today)
                date.setDate(today.getDate() + i)
                return {
                    date: date.toISOString().slice(0, 10),
                    temp_min: d.min + (i % 3),
                    temp_max: d.max - (i % 2),
                    precipitation_probability: d.id > 40 ? 60 + (i * 5) : 10,
                    condition: { id: d.id, icon: d.icon },
                    sunrise: '06:45',
                    sunset: '18:12',
                }
            }),
        },
    }))
    state.activeIndex = 0
    state.defaultIndex = 0
    state._demo = true
}

function fetchDefault() {
    if (state.locations.length) fetchWeather(state.defaultIndex)
}

function fetchIfNeeded(index) {
    const loc = state.locations[index]
    if (loc && loc.status !== 'loaded' && loc.status !== 'loading') {
        fetchWeather(index)
    }
}

function fetchStale() {
    const promises = state.locations
        .map((l, i) => (l.status !== 'loaded' && l.status !== 'loading') ? fetchWeather(i) : null)
        .filter(Boolean)
    return Promise.allSettled(promises)
}

function init() {
    if (state._initialized) return
    state._initialized = true
    loadSaved()
    if (state.locations.length) {
        state._ready = true
        fetchDefault()
    } else {
        UserConfigService.whenReady().then(() => {
            if (!state.locations.length) loadSaved()
            state._ready = true
            if (state.locations.length) fetchDefault()
        })
    }
    startAutoRefresh()
}

function onPanelOpen() {
    init()
    state.activeIndex = state.defaultIndex
    fetchStale()
}

function destroy() {
    stopAutoRefresh()
    state._initialized = false
}

function toggleDockAnimation() {
    state.dockAnimation = !state.dockAnimation
    save()
}

const defaultLocation = computed(() => state.locations[state.defaultIndex] || null)
const activeLocation = computed(() => state.locations[state.activeIndex] || null)

export default {
    state,
    defaultLocation,
    activeLocation,
    init,
    destroy,
    fetchWeather,
    fetchAll,
    addLocation,
    removeLocation,
    setDefault,
    setActive,
    searchCity,
    detectLocation,
    save,
    loadDemo,
    toggleDockAnimation,
    onPanelOpen,
}
